import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BrandedLoader from './components/shared/BrandedLoader';
import ErrorBoundary from './components/shared/ErrorBoundary';
import ProfileLoadError from './components/shared/ProfileLoadError';
import IdleTimeoutGuard from './components/shared/IdleTimeoutGuard';
import './styles/globals.css';

// Split per role. The therapist portal is most of the app by weight, and a
// client on gym wifi has no use for any of it — loading both statically made
// every client download ClientManager, ExerciseLibrary and the sessions views
// before they could see their first exercise. The role is known before either
// renders, so only one chunk is ever fetched.
const ClientDashboard = lazy(() => import('./components/client/ClientDashboard'));
const PausedAccountView = lazy(() => import('./components/client/PausedAccountView'));
const TherapistDashboard = lazy(() => import('./components/therapist/TherapistDashboard'));

function AppRoutes() {
  const { user, profile, loading, profileError, needsPasswordReset } = useAuth();

  if (loading) {
    return <BrandedLoader />;
  }

  if (needsPasswordReset) {
    return <ResetPasswordPage />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  // Signed in but the profile would not load. Falling through to the login page
  // here is what made a network blip look like a random logout.
  if (!profile) {
    return profileError ? <ProfileLoadError /> : <BrandedLoader />;
  }

  return (
    <IdleTimeoutGuard>
      <Suspense fallback={<BrandedLoader firstName={profile.full_name?.split(' ')[0]} />}>
        <Routes>
          <Route
            path="/*"
            element={
              profile.role === 'therapist'
                ? <TherapistDashboard />
                : profile.paused_at
                ? <PausedAccountView />
                : <ClientDashboard />
            }
          />
        </Routes>
      </Suspense>
    </IdleTimeoutGuard>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
